/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { Select, SelectOnChangeType, useDirection } from '@veneer/core';
import React, {
  forwardRef,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo
} from 'react';
import './styles.scss';
import { SelectInputProps } from './types';
import { withAnalytics } from '@veneer/analytics';
import { generateID } from '../../base/helper';

/**
 * UI Toolkit Select Input component based
 * @public
 */
const TSelectInput = forwardRef<HTMLInputElement, SelectInputProps>(
  (
    {
      items,
      onValueChange,
      disabled,
      invalid,
      label,
      focused,
      helperText,
      id = generateID('clientos-ui-toolkit-select-input'),
      ...rest
    },
    ref
  ) => {
    const [focus, setFocus] = useState(focused || false);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const selectButtonRef = useRef<HTMLElement | null>(null);

    const handleChange: SelectOnChangeType<string | number> = (option) => {
      if (onValueChange && option.value !== undefined) {
        onValueChange(option.value);
      }
    };

    const isRtl = useDirection().isRTL;

    const handleFocusToggle = () => {
      if (!disabled) {
        setFocus(true);
      }
    };

    const handleDropdownMenu = useCallback(() => {
      const listBox = document.getElementById(`${id}-listbox`);
      const menuPlacement = listBox?.parentElement?.parentElement?.getAttribute(
        'data-popper-placement'
      );

      const firstElement = listBox?.childNodes[0] as HTMLElement;
      firstElement?.scrollIntoView({ block: 'start', inline: 'nearest' });

      for (let index = 0; index < listBox?.childNodes?.length; index++) {
        const element = listBox?.childNodes[index] as HTMLElement;
        if (element.getAttribute('aria-selected') === 'true') {
          element.scrollIntoView({ block: 'start', inline: 'nearest' });
          break;
        }
      }

      if (menuPlacement === 'bottom-end') {
        const maxAvailableArea =
          window.innerHeight -
          listBox?.parentElement?.parentElement?.getBoundingClientRect().top -
          18;
        listBox?.parentElement?.style?.setProperty(
          'max-height',
          `${maxAvailableArea}px`
        );
      }

      if (isRtl) {
        listBox?.classList.add('rtl');
      } else {
        listBox?.classList.remove('rtl');
      }
    }, [id, isRtl]);

    useEffect(() => {
      if (isOpen) {
        handleDropdownMenu();
      }
    }, [handleDropdownMenu, isOpen]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setFocus(false);
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    useEffect(() => {
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const listBox = document.getElementById(`${id}-listbox`);
            setIsOpen(!!listBox);
          }
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      return () => observer.disconnect();
    }, [id]);

    useEffect(() => {
      const button = containerRef.current?.querySelector<HTMLElement>(
        '.vn-select__open-button'
      );
      if (!button) return;

      selectButtonRef.current = button;

      button.removeAttribute('title');
      button.removeAttribute('aria-label');

      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.key === 'Tab' || e.key === 'Escape') && isOpen) {
          e.preventDefault();
          e.stopPropagation();

          setIsOpen(false);

          requestAnimationFrame(() => {
            button.focus();
          });
        }
      };

      button.addEventListener('keydown', handleKeyDown);

      return () => {
        button.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen]);

    const getClassName = useMemo(() => {
      let className = 'clientos-ui-toolkit-select-input select-input-container';
      if (label) className += ' clientos-ui-toolkit-select-wrapper-with-label';
      if (invalid && !disabled)
        className += ' clientos-ui-toolkit-select-input--invalid';
      if (disabled) className += ' clientos-ui-toolkit-select-input--disabled';
      if (focus) className += ' focused';
      if (isOpen) className += ' clientos-ui-toolkit-select-input--is-open';
      if (isRtl) className += ' rtl';
      return className;
    }, [disabled, focus, invalid, isOpen, label, isRtl]);

    return (
      <div
        className={`clientos-ui-toolkit-select-wrapper`}
        id={id}
        ref={containerRef}
      >
        <Select
          id={`${id}-select`}
          ref={ref}
          options={items}
          onChange={handleChange as any}
          onFocus={handleFocusToggle}
          onBlur={(e) => {
            const related = e.relatedTarget as HTMLElement | null;
            if (!containerRef.current?.contains(related)) {
              setFocus(false);
              setIsOpen(false);
            }
          }}
          disabled={disabled}
          label={label}
          clearIcon={false}
          helperText={helperText}
          helperTextVisibility="visible"
          className={getClassName}
          portalContainer={() => containerRef.current || document.body}
          {...rest}
        />
      </div>
    );
  }
);

TSelectInput.displayName = 'SelectInput';

export const SelectInputAnalytics = withAnalytics(TSelectInput);
export default TSelectInput;
